import React from 'react';
import { FiDownload } from 'react-icons/fi';
import Button from './Button';

const escapeCell = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    if (typeof value.toDate === 'function') return value.toDate().toISOString();
    if (value instanceof Date) return value.toISOString();
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  const str = String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const buildCsv = (rows, columns) => {
  if (!rows.length) return '';
  const cols = columns ||
    Object.keys(rows[0]).map((key) => ({ key, label: key }));
  const header = cols.map((c) => escapeCell(c.label || c.key)).join(',');
  const body = rows.map((row) =>
    cols.map((c) => {
      const raw = c.accessor ? c.accessor(row) : row[c.key];
      return escapeCell(raw);
    }).join(',')
  );
  return [header, ...body].join('\n');
};

/**
 * Export an array of objects to CSV.
 * Props:
 *   - rows: array of records to export
 *   - columns: [{ key, label?, accessor? }] (optional — defaults to row keys)
 *   - filename: file name to download (without extension)
 *   - children: button label (default "Export CSV")
 */
const CsvExportButton = ({
  rows,
  columns,
  filename = 'export',
  children,
  disabled,
  variant = 'ghost',
  size = 'sm',
}) => {
  const handleClick = () => {
    const csv = buildCsv(rows || [], columns);
    if (!csv) return;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    link.href = url;
    link.download = `${filename}-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Button
      variant={variant}
      size={size}
      icon={<FiDownload />}
      onClick={handleClick}
      disabled={disabled || !rows || rows.length === 0}
    >
      {children || 'Export CSV'}
    </Button>
  );
};

export default CsvExportButton;
